import moment from 'moment';
import { WaitingListEntry } from './lockers.models';

/** Two letters at most, so the circle never has to shrink its type to fit a long name. */
export function initialsFor(name?: string): string {
  if (!name) return '';
  return name
    .split(' ')
    .filter((part: string) => part.length > 0)
    .slice(0, 2)
    .map((part: string) => part[0].toUpperCase())
    .join('');
}

/**
 * Lockers are labelled by row in most offices ("A04" is the fourth locker in row A), so the row is
 * worth showing when the label follows that shape and worth staying quiet about when it does not.
 */
export function rowFromLabel(label?: string): string | null {
  const match = (label ?? '').match(/^([A-Za-z])\s*\d/);
  return match ? match[1].toUpperCase() : null;
}

export function daysWaiting(entry: WaitingListEntry): number {
  return moment().startOf('day').diff(moment(entry.createdAt).startOf('day'), 'days');
}

export function averageDaysWaiting(entries: WaitingListEntry[]): number {
  if (entries.length === 0) return 0;
  const total = entries.reduce((sum: number, entry: WaitingListEntry) => sum + daysWaiting(entry), 0);
  return Math.round(total / entries.length);
}

export function longestWait(entries: WaitingListEntry[]): number {
  return entries.reduce((longest: number, entry: WaitingListEntry) => Math.max(longest, daysWaiting(entry)), 0);
}

export function formatDate(date?: string): string {
  return date ? moment(date).format('D MMM YYYY') : '';
}

/** Ordinals read better than "position 2" in a sentence about your own place in a queue. */
export function ordinal(position: number): string {
  const remainderOfTen = position % 10;
  const remainderOfHundred = position % 100;
  if (remainderOfTen === 1 && remainderOfHundred !== 11) return `${position}st`;
  if (remainderOfTen === 2 && remainderOfHundred !== 12) return `${position}nd`;
  if (remainderOfTen === 3 && remainderOfHundred !== 13) return `${position}rd`;
  return `${position}th`;
}
